import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { ToastService } from '../../../modules/shared/services/toast-message.service';
import { TeamService } from '../../../services/team/team.service';
import { PagedResponse } from '../general/models/general';
import { IInvitation } from './models/invitations';
import { ITeamMember } from './models/team';
import * as actions from './team.actions';

@Injectable()
export class TeamEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: TeamService,
    private readonly toastr: ToastService,
  ) {}

  getAllTeamMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getAllTeamMembers),
      exhaustMap(({ searchTerm, pageNumber }) =>
        this.service.getAllTeamMembers({ searchTerm, pageNumber }).pipe(
          map((pagedTeamMembers: PagedResponse<ITeamMember>) => {
            return actions.getAllTeamMembersSuccess({ pagedTeamMembers });
          }),
          catchError((error: any) =>
            of(actions.getAllTeamMembersFailure(error)),
          ),
        ),
      ),
    ),
  );

  removeTeamMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeTeamMember),
      exhaustMap(({ userId }) =>
        this.service.removeTeamMember(userId).pipe(
          map(() => {
            this.toastr.success('Team member removed.');
            return actions.removeTeamMemberSuccess();
          }),
          catchError((error: any) =>
            of(actions.removeTeamMemberFailure(error)),
          ),
        ),
      ),
    ),
  );

  removeTeamMemberSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeTeamMemberSuccess),
      map(() => actions.getAllTeamMembers({ pageNumber: 1 })),
    ),
  );

  editTeamMemberRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.editTeamMemberRole),
      exhaustMap(({ userId, newRole }) =>
        this.service.editTeamMemberRole(userId, newRole).pipe(
          map(() => {
            this.toastr.success('Team member updated.');
            return actions.editTeamMemberRoleSuccess();
          }),
          catchError((error: any) =>
            of(actions.editTeamMemberRoleFailure(error)),
          ),
        ),
      ),
    ),
  );

  editTeamMemberRoleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.editTeamMemberRoleSuccess),
      map(() => actions.getAllTeamMembers({ pageNumber: 1 })),
    ),
  );

  inviteTeamMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.inviteTeamMember),
      exhaustMap(({ email, role }) =>
        this.service.inviteTeamMember(email, role).pipe(
          map(() => {
            this.toastr.success(`Invitation sent to '${email}'.`);
            return actions.inviteTeamMemberSuccess();
          }),
          catchError((error: any) =>
            of(actions.inviteTeamMemberFailure(error)),
          ),
        ),
      ),
    ),
  );

  getInvitations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getInvitations),
      exhaustMap(({ searchTerm, pageNumber }) =>
        this.service.getInvitations({ searchTerm, pageNumber }).pipe(
          map((pagedInvitations: PagedResponse<IInvitation>) => {
            return actions.getInvitationsSuccess({ pagedInvitations });
          }),
          catchError((error: any) => of(actions.getInvitationsFailure(error))),
        ),
      ),
    ),
  );

  inviteTeamMemberSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.inviteTeamMemberSuccess),
      map(() => actions.getInvitations({ pageNumber: 1 })),
    ),
  );

  resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.resendInvitation),
      exhaustMap(({ invitationId }) =>
        this.service.resendInvitation(invitationId).pipe(
          map(() => {
            this.toastr.success('Invitation resent.');
            return actions.resendInvitationSuccess();
          }),
          catchError((error: any) =>
            of(actions.resendInvitationFailure(error)),
          ),
        ),
      ),
    ),
  );

  resendInvitationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.resendInvitationSuccess),
      map(() => actions.getInvitations({ pageNumber: 1 })),
    ),
  );

  deleteInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteInvitation),
      exhaustMap(({ invitationId }) =>
        this.service.removeInvitation(invitationId).pipe(
          map(() => {
            this.toastr.success('Invitation has been removed.');
            return actions.deleteInvitationSuccess();
          }),
          catchError((error: any) =>
            of(actions.deleteInvitationFailure(error)),
          ),
        ),
      ),
    ),
  );

  deleteInvitationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteInvitationSuccess),
      map(() => actions.getInvitations({ pageNumber: 1 })),
    ),
  );
}
